import {
  Box,
  MenuItem,
  MenuList,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { ChipOption } from "../../types";

// ─── Types ───────────────────────────────────────────────────────────

export interface VariableChipListRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

interface VariableChipListProps {
  items: ChipOption[];
  command: (item: ChipOption) => void;
}

// ─── Component ───────────────────────────────────────────────────────

export const VariableChipList = forwardRef<
  VariableChipListRef,
  VariableChipListProps
>(({ items, command }, ref) => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const selected = container.children[selectedIndex] as HTMLElement | undefined;
    selected?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const selectItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;
      command(item);
    },
    [items, command],
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent): boolean => {
      if (items.length === 0) return false;

      if (event.key === "ArrowUp") {
        setSelectedIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
        return true;
      }

      if (event.key === "ArrowDown") {
        setSelectedIndex((prev) => (prev >= items.length - 1 ? 0 : prev + 1));
        return true;
      }

      if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          borderRadius: "8px",
          minWidth: "200px",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.lightBg.low,
            px: "12px",
            py: "8px",
          }}
        >
          No matching variables
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        maxHeight: "240px",
        overflow: "auto",
        borderRadius: "16px",
        minWidth: "200px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body-small"
        sx={{
          color: theme.palette.lightBg.low,
          px: "12px",
          py: "8px",
          display: "block",
        }}
      >
        Variables
      </Typography>
      <MenuList ref={listRef} sx={{ py: "4px" }}>
        {items.map((item, index) => (
          <MenuItem
            key={item.id}
            onClick={() => selectItem(index)}
            selected={index === selectedIndex}
            sx={{
              gap: "8px",
              py: "6px",
              px: "12px",
              borderRadius: "4px",
              mx: "4px",
              "&.Mui-selected": {
                backgroundColor: theme.palette.primitive.system.violet[25],
              },
              "&:hover": {
                backgroundColor: theme.palette.primitive.system.violet[25],
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "24px",
                gap: "8px",
                border: `1px solid ${theme.palette.primary.tint}`,
                borderRadius: "4px",
                padding: "2px 6px",
                backgroundColor: theme.palette.primary.tint,
              }}
            >
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.primary.main }}
              >
                {item.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </Paper>
  );
});

VariableChipList.displayName = "VariableChipList";
